import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Switch,
  Card,
  Typography,
  Tag,
  Form,
  Alert,
  Input,
  Select,
  Space,
  message,
  Spin,
  InputNumber,
  Button
} from "antd";
import { ExclamationCircleOutlined, CheckOutlined } from "@ant-design/icons";
import { doGetTiers } from "../../../tiers/store/thunks";
import { selectAllTiers } from "../../../tiers/store/tierSlice";
import clientApi from "../../store/api";

const { Title, Text } = Typography;

// Internal Drivers entitlement ID
const INTERNAL_DRIVERS_ENTITLEMENT_ID = "5c2b11b0-7ed9-410c-b736-8b1fe3aba06a";

// SFTP POD Upload entitlement ID
const SFTP_POD_ENTITLEMENT_ID = "d38eafe1-6679-4d6b-8161-38b34f9bec2a";

// eslint-disable-next-line no-misleading-character-class -- intentionally matching individual zero-width/invisible Unicode characters; no ranges used
const ZERO_WIDTH_REGEX = /[\u200B\u200C\u200D\uFEFF\u034F\u2060\u2800\u3164\uFFA0]/;
const FQDN_OR_IP_REGEX =
  /^(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,63}|(?:25[0-5]|2[0-4]\d|[01]?\d\d?)(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d\d?)){3})$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateSftpPodSettings = (settings) => {
  const errors = [];

  if (!settings.sftpHost) {
    errors.push("SFTP host is required");
  } else if (settings.sftpHost.length > 253) {
    errors.push("SFTP host must be 253 characters or less");
  } else if (!FQDN_OR_IP_REGEX.test(settings.sftpHost)) {
    errors.push("SFTP host must be a valid domain name or IPv4 address");
  } else if (ZERO_WIDTH_REGEX.test(settings.sftpHost)) {
    errors.push("SFTP host must not contain invisible characters");
  }

  const port = Number(settings.sftpPort);
  if (!settings.sftpPort) {
    errors.push("SFTP port is required");
  } else if (!Number.isInteger(port) || port < 1 || port > 65535) {
    errors.push("SFTP port must be a whole number between 1 and 65535");
  }

  if (!settings.sftpUser) {
    errors.push("SFTP username is required");
  } else if (settings.sftpUser.length > 64) {
    errors.push("SFTP username must be 64 characters or less");
  } else if (ZERO_WIDTH_REGEX.test(settings.sftpUser)) {
    errors.push("SFTP username must not contain invisible characters");
  }

  if (!settings.sftpPassword) {
    errors.push("SFTP password is required");
  } else if (settings.sftpPassword.length > 256) {
    errors.push("SFTP password must be 256 characters or less");
  } else if (ZERO_WIDTH_REGEX.test(settings.sftpPassword)) {
    errors.push("SFTP password must not contain invisible characters");
  }

  if (settings.sftpPath) {
    if (!settings.sftpPath.startsWith("/")) {
      errors.push("SFTP remote path must start with /");
    } else if (settings.sftpPath.length > 512) {
      errors.push("SFTP remote path must be 512 characters or less");
    } else if (ZERO_WIDTH_REGEX.test(settings.sftpPath)) {
      errors.push("SFTP remote path must not contain invisible characters");
    }
  }

  if (settings.notificationEmail) {
    if (!EMAIL_REGEX.test(settings.notificationEmail)) {
      errors.push("Notification email must be a valid email address");
    } else if (settings.notificationEmail.length > 254) {
      errors.push("Notification email must be 254 characters or less");
    }
  }

  return errors;
};

const BillingSettingsTab = forwardRef(({ client, onChange }, ref) => {
  const dispatch = useDispatch();
  const tiers = useSelector(selectAllTiers);

  // State for billing settings and client status
  const [combineInvoiceReceipt, setCombineInvoiceReceipt] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [tierId, setTierId] = useState(null);
  const [facturaPaymentTerms, setFacturaPaymentTerms] = useState(1);
  const [form] = Form.useForm();

  // State for entitlements
  const [realEntitlements, setRealEntitlements] = useState([]);
  const [entitlementsLoading, setEntitlementsLoading] = useState(true);

  // State for Internal Drivers seat configuration
  const [internalDriversSeats, setInternalDriversSeats] = useState({});
  const [internalDriversSeatsSaving, setInternalDriversSeatsSaving] = useState({});

  // State for SFTP POD settings
  const [sftpPodSettings, setSftpPodSettings] = useState({});
  const [sftpPodSettingsSaving, setSftpPodSettingsSaving] = useState(false);

  // Fetch tiers on mount
  useEffect(() => {
    console.log("BillingSettingsTab: Fetching tiers...");
    dispatch(doGetTiers());
  }, [dispatch]);

  useEffect(() => {
    console.log("BillingSettingsTab: Tiers from Redux:", tiers);
  }, [tiers]);

  useEffect(() => {
    // Initialize billing settings from client data if available
    if (client) {
      console.log("BillingSettingsTab: Client data:", client);
      setIsBlocked(client?.isBlocked || false);
      // Handle both undefined and null, but preserve the tierId if it exists
      const clientTierId = client?.tierId !== undefined ? client.tierId : null;
      setTierId(clientTierId);
      setFacturaPaymentTerms(client?.facturaPaymentTerms || 1);
      fetchClientEntitlements();
    }
  }, [client]);

  const fetchClientEntitlements = async () => {
    if (!client?.id) return;

    try {
      setEntitlementsLoading(true);
      const response = await clientApi.getClientEntitlements(client.id);
      setRealEntitlements(response || []);

      // Initialize seat configuration for Internal Drivers entitlement
      const internalDriversEnt = response?.find(
        (e) => e.id === INTERNAL_DRIVERS_ENTITLEMENT_ID
      );
      if (internalDriversEnt) {
        setInternalDriversSeats((prev) => ({
          ...prev,
          [INTERNAL_DRIVERS_ENTITLEMENT_ID]: {
            seats: internalDriversEnt.settings?.seats || 0,
            perSeatCost:
              internalDriversEnt.settings?.perSeatCost !== undefined
                ? internalDriversEnt.settings.perSeatCost
                : parseFloat(internalDriversEnt.cost) || 0
          }
        }));
      }

      // Initialize SFTP POD settings
      const sftpPodEnt = response?.find((e) => e.id === SFTP_POD_ENTITLEMENT_ID);
      if (sftpPodEnt) {
        setSftpPodSettings({
          sftpHost: sftpPodEnt.settings?.sftpHost || "",
          sftpPort: sftpPodEnt.settings?.sftpPort || 22,
          sftpUser: sftpPodEnt.settings?.sftpUser || "",
          sftpPassword: sftpPodEnt.settings?.sftpPassword || "",
          sftpPath: sftpPodEnt.settings?.sftpPath || "/",
          notificationEmail: sftpPodEnt.settings?.notificationEmail || "",
          emailOnFailureOnly:
            sftpPodEnt.settings?.emailOnFailureOnly !== undefined
              ? sftpPodEnt.settings.emailOnFailureOnly
              : true
        });
      }
    } catch (error) {
      console.error("Error fetching client entitlements:", error);
      message.error("Failed to load entitlements");
      setRealEntitlements([]);
    } finally {
      setEntitlementsLoading(false);
    }
  };

  const handleRealEntitlementToggle = async (entitlementId, isActive) => {
    try {
      // For Internal Drivers, get the current settings if available
      let settings = undefined;
      if (entitlementId === SFTP_POD_ENTITLEMENT_ID) {
        // Never send settings during a toggle — only active status changes here.
        // Settings are updated exclusively via the "Save SFTP Settings" button.
        // This prevents any DTO validation error from blocking the toggle.
        settings = undefined;
      } else if (entitlementId === INTERNAL_DRIVERS_ENTITLEMENT_ID) {
        const currentSeats = internalDriversSeats[INTERNAL_DRIVERS_ENTITLEMENT_ID];
        if (currentSeats) {
          settings = currentSeats;
        } else if (isActive) {
          // Initialize default settings when enabling for the first time
          const entitlement = realEntitlements.find((e) => e.id === entitlementId);
          const costValue = parseFloat(entitlement?.cost);
          const defaultSettings = {
            seats: 1,
            perSeatCost: !isNaN(costValue) ? costValue : 17.99
          };
          console.log(
            "Initializing Internal Drivers with default settings:",
            defaultSettings
          );
          settings = defaultSettings;
          // Update the local state so the UI shows the default values
          setInternalDriversSeats((prev) => ({
            ...prev,
            [entitlementId]: defaultSettings
          }));
        }
      }

      console.log("Updating client entitlement:", {
        clientId: client.id,
        entitlementId,
        isActive,
        settings
      });

      await clientApi.updateClientEntitlement(
        client.id,
        entitlementId,
        isActive,
        settings
      );

      // Update local state — only overwrite settings if they were actually sent
      setRealEntitlements((prev) =>
        prev.map((entitlement) =>
          entitlement.id === entitlementId
            ? {
                ...entitlement,
                client_is_active: isActive,
                ...(settings !== undefined ? { settings } : {})
              }
            : entitlement
        )
      );

      message.success("Entitlement updated successfully");
    } catch (error) {
      console.error("Error updating entitlement:", error);
      console.error("Error response:", error.response?.data);
      message.error("Failed to update entitlement");
    }
  };

  const handleInternalDriversSeatsChange = (entitlementId, field, value) => {
    setInternalDriversSeats((prev) => ({
      ...prev,
      [entitlementId]: {
        ...prev[entitlementId],
        [field]: value
      }
    }));
  };

  const handleSaveInternalDriversSeats = async (entitlementId) => {
    try {
      setInternalDriversSeatsSaving((prev) => ({ ...prev, [entitlementId]: true }));

      const entitlement = realEntitlements.find((e) => e.id === entitlementId);
      const isActive = entitlement?.client_is_active || false;
      const settings = internalDriversSeats[entitlementId];

      await clientApi.updateClientEntitlement(
        client.id,
        entitlementId,
        isActive,
        settings
      );

      // Update local state
      setRealEntitlements((prev) =>
        prev.map((e) => (e.id === entitlementId ? { ...e, settings: settings } : e))
      );

      message.success("Seat configuration updated successfully");
    } catch (error) {
      console.error("Error updating seat configuration:", error);
      message.error("Failed to update seat configuration");
    } finally {
      setInternalDriversSeatsSaving((prev) => ({ ...prev, [entitlementId]: false }));
    }
  };

  const handleSaveSftpPodSettings = async () => {
    const errors = validateSftpPodSettings(sftpPodSettings);
    if (errors.length > 0) {
      errors.forEach((err) => message.error(err, 4));
      return;
    }

    try {
      setSftpPodSettingsSaving(true);
      const entitlement = realEntitlements.find((e) => e.id === SFTP_POD_ENTITLEMENT_ID);
      const isActive = entitlement?.client_is_active || false;

      // Strip empty strings so @IsOptional() backend validators are skipped for blank optional
      // fields (e.g. notificationEmail: "" would otherwise fail @IsEmail()).
      const settingsPayload = Object.fromEntries(
        Object.entries(sftpPodSettings).filter(([, v]) => v !== "")
      );
      await clientApi.updateClientEntitlement(
        client.id,
        SFTP_POD_ENTITLEMENT_ID,
        isActive,
        settingsPayload
      );

      setRealEntitlements((prev) =>
        prev.map((e) =>
          e.id === SFTP_POD_ENTITLEMENT_ID ? { ...e, settings: sftpPodSettings } : e
        )
      );

      message.success("SFTP settings updated successfully");
    } catch (error) {
      console.error("Error updating SFTP settings:", error);
      message.error("Failed to update SFTP settings");
    } finally {
      setSftpPodSettingsSaving(false);
    }
  };

  // Handlers for billing settings
  const handleTierChange = (value) => {
    setTierId(value);
    if (onChange) onChange();
  };

  const handleFacturaPaymentTermsChange = (value) => {
    setFacturaPaymentTerms(value);
    if (onChange) onChange();
  };

  const handleCombineInvoiceReceiptChange = (checked) => {
    setCombineInvoiceReceipt(checked);
    if (onChange) onChange();
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    getFormData: () => {
      return {
        isBlocked,
        tierId,
        facturaPaymentTerms,
        blockReason: form.getFieldValue("blockReason")
      };
    }
  }));

  return (
    <div className="billing-settings-tab">
      <Form form={form} layout="vertical">
        {/* 2-Column Layout */}
        <div
          className="settings-row"
          style={{ display: "flex", gap: "16px", marginBottom: "24px" }}
        >
          {/* LEFT COLUMN - Combined Billing Settings */}
          <div className="settings-left-column" style={{ flex: 1 }}>
            <Card
              title="Billing Settings"
              className="billing-settings-card"
              style={{ height: "100%" }}
            >
              {/* Factura Payment Terms Selection */}
              <Form.Item label="Factura Payment Terms" style={{ marginBottom: "24px" }}>
                <Select
                  value={facturaPaymentTerms}
                  onChange={handleFacturaPaymentTermsChange}
                  size="large"
                  placeholder="Please Select Payment Terms"
                >
                  <Select.Option value={-1}>Manual Bill Only</Select.Option>
                  <Select.Option value={0}>Pre-pay Required</Select.Option>
                  <Select.Option value={1}>Daily</Select.Option>
                  <Select.Option value={7}>Weekly</Select.Option>
                  <Select.Option value={30}>Monthly</Select.Option>
                </Select>
              </Form.Item>

              {/* Tier Selection */}
              <Form.Item label="Current Tier" style={{ marginBottom: "24px" }}>
                <Select
                  value={tierId || undefined}
                  onChange={handleTierChange}
                  size="large"
                  placeholder="Please Select Tier"
                  allowClear
                  onClear={() => handleTierChange(null)}
                >
                  <Select.Option value={undefined}>No Tier</Select.Option>
                  {tiers
                    .filter((tier) => tier.isActive)
                    .map((tier) => (
                      <Select.Option key={tier.id} value={tier.id}>
                        {tier.name} - ${Number(tier.cost).toFixed(2)}/{tier.period}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              {/* Billing Toggles */}
              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                <div className="billing-toggle">
                  <div className="toggle-content">
                    <div className="toggle-info">
                      <Title level={5}>Combine Invoice and Receipt</Title>
                      <Text type="secondary">
                        Combine invoice and receipt into a single document
                      </Text>
                    </div>
                    <Switch
                      checked={combineInvoiceReceipt}
                      onChange={handleCombineInvoiceReceiptChange}
                      checkedChildren="ON"
                      unCheckedChildren="OFF"
                    />
                  </div>
                </div>
              </Space>
            </Card>
          </div>

          {/* RIGHT COLUMN - Client Status */}
          <div className="settings-right-column" style={{ flex: 1 }}>
            {client && (
              <Card
                title="Client Status"
                className="client-status-card"
                style={{ height: "100%" }}
              >
                <div
                  style={{
                    padding: "16px",
                    backgroundColor: isBlocked ? "#ffebee" : "#f5f5f5",
                    borderRadius: "4px",
                    border: isBlocked ? "1px solid #ffcdd2" : "1px solid #d9d9d9"
                  }}
                >
                  <Form.Item label="Client Status" style={{ marginBottom: "16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <Switch
                        checked={isBlocked}
                        onChange={(checked) => {
                          setIsBlocked(checked);
                          if (onChange) onChange();
                        }}
                        checkedChildren="Blocked"
                        unCheckedChildren="Active"
                        className={isBlocked ? "blocked-switch" : ""}
                      />
                      <span
                        style={{
                          fontWeight: "500",
                          color: isBlocked ? "#ff4d4f" : undefined
                        }}
                      >
                        {isBlocked ? "Client is blocked" : "Client is active"}
                      </span>
                    </div>
                  </Form.Item>

                  {isBlocked && (
                    <>
                      <Alert
                        message="Client is Currently Blocked"
                        description={
                          <div>
                            <p>
                              <strong>Blocked At:</strong>{" "}
                              {client.blockedAt
                                ? new Date(client.blockedAt).toLocaleString()
                                : "N/A"}
                            </p>
                            <p>
                              <strong>Blocked By:</strong> {client.blockedBy || "N/A"}
                            </p>
                            {client.blockReason && (
                              <p>
                                <strong>Block Reason:</strong> {client.blockReason}
                              </p>
                            )}
                          </div>
                        }
                        type="error"
                        showIcon
                        icon={<ExclamationCircleOutlined />}
                        style={{ marginBottom: "16px" }}
                      />

                      <Form.Item
                        label="Reason for blocking (required):"
                        name="blockReason"
                        rules={[
                          {
                            required: true,
                            message: "Block reason is required when blocking a client!"
                          }
                        ]}
                        initialValue={client.blockReason}
                      >
                        <Input.TextArea
                          rows={3}
                          placeholder="Enter detailed reason for blocking this client (e.g., payment issues, policy violations, etc.)"
                          maxLength={500}
                          onChange={() => {
                            if (onChange) onChange();
                          }}
                        />
                      </Form.Item>
                    </>
                  )}
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Entitlements Section */}
        <div style={{ marginTop: "32px" }}>
          <Title level={4} style={{ marginBottom: "16px" }}>
            Entitlements
          </Title>

          <Spin spinning={entitlementsLoading}>
            <Card className="entitlements-card">
              {realEntitlements.length > 0 ? (
                <div className="entitlements-list">
                  {realEntitlements.map((entitlement) => (
                    <div className="entitlement-toggle" key={entitlement.id}>
                      <div className="entitlement-content">
                        <div className="entitlement-info">
                          <Text strong>{entitlement.name}</Text>
                          <Text
                            type="secondary"
                            style={{ fontSize: "12px", display: "block" }}
                          >
                            {entitlement.description}
                          </Text>
                          {entitlement.cost && (
                            <Text
                              type="secondary"
                              style={{ fontSize: "11px", color: "#1890ff" }}
                            >
                              ${entitlement.cost}{" "}
                              {entitlement.term === 0
                                ? "/ea"
                                : entitlement.term === 30
                                ? "/month"
                                : ""}
                            </Text>
                          )}

                          {/* SFTP POD Upload configuration */}
                          {entitlement.id === SFTP_POD_ENTITLEMENT_ID &&
                            entitlement.client_is_active && (
                              <div
                                style={{
                                  marginTop: "12px",
                                  padding: "8px",
                                  background: "#f0f5ff",
                                  borderRadius: "4px",
                                  border: "1px solid #d6e4ff"
                                }}
                              >
                                <div style={{ marginBottom: "8px" }}>
                                  <Text
                                    style={{
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      display: "block",
                                      marginBottom: "6px",
                                      color: "#1d39c4"
                                    }}
                                  >
                                    SFTP Connection
                                  </Text>
                                  <div style={{ marginBottom: "6px" }}>
                                    <Text
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: "500",
                                        display: "block",
                                        marginBottom: "3px"
                                      }}
                                    >
                                      Host:
                                    </Text>
                                    <Input
                                      size="small"
                                      placeholder="sftp.example.com"
                                      value={sftpPodSettings.sftpHost || ""}
                                      onChange={(e) =>
                                        setSftpPodSettings((prev) => ({
                                          ...prev,
                                          sftpHost: e.target.value
                                        }))
                                      }
                                    />
                                  </div>
                                  <div style={{ marginBottom: "6px" }}>
                                    <Text
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: "500",
                                        display: "block",
                                        marginBottom: "3px"
                                      }}
                                    >
                                      Port:
                                    </Text>
                                    <InputNumber
                                      size="small"
                                      min={1}
                                      max={65535}
                                      value={sftpPodSettings.sftpPort || 22}
                                      onChange={(value) =>
                                        setSftpPodSettings((prev) => ({
                                          ...prev,
                                          sftpPort: value
                                        }))
                                      }
                                      style={{ width: "100%" }}
                                    />
                                  </div>
                                  <div style={{ marginBottom: "6px" }}>
                                    <Text
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: "500",
                                        display: "block",
                                        marginBottom: "3px"
                                      }}
                                    >
                                      Username:
                                    </Text>
                                    <Input
                                      size="small"
                                      placeholder="sftp_user"
                                      value={sftpPodSettings.sftpUser || ""}
                                      onChange={(e) =>
                                        setSftpPodSettings((prev) => ({
                                          ...prev,
                                          sftpUser: e.target.value
                                        }))
                                      }
                                    />
                                  </div>
                                  <div style={{ marginBottom: "6px" }}>
                                    <Text
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: "500",
                                        display: "block",
                                        marginBottom: "3px"
                                      }}
                                    >
                                      Password:
                                    </Text>
                                    <Input.Password
                                      size="small"
                                      placeholder="••••••••"
                                      value={sftpPodSettings.sftpPassword || ""}
                                      onChange={(e) =>
                                        setSftpPodSettings((prev) => ({
                                          ...prev,
                                          sftpPassword: e.target.value
                                        }))
                                      }
                                    />
                                  </div>
                                  <div style={{ marginBottom: "10px" }}>
                                    <Text
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: "500",
                                        display: "block",
                                        marginBottom: "3px"
                                      }}
                                    >
                                      Remote Path:
                                    </Text>
                                    <Input
                                      size="small"
                                      placeholder="/uploads/pod/"
                                      value={sftpPodSettings.sftpPath || ""}
                                      onChange={(e) =>
                                        setSftpPodSettings((prev) => ({
                                          ...prev,
                                          sftpPath: e.target.value
                                        }))
                                      }
                                    />
                                  </div>
                                  <Text
                                    style={{
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      display: "block",
                                      marginBottom: "6px",
                                      color: "#1d39c4"
                                    }}
                                  >
                                    Notifications
                                  </Text>
                                  <div style={{ marginBottom: "6px" }}>
                                    <Text
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: "500",
                                        display: "block",
                                        marginBottom: "3px"
                                      }}
                                    >
                                      Notification Email:
                                    </Text>
                                    <Input
                                      size="small"
                                      placeholder="ops@example.com"
                                      value={sftpPodSettings.notificationEmail || ""}
                                      onChange={(e) =>
                                        setSftpPodSettings((prev) => ({
                                          ...prev,
                                          notificationEmail: e.target.value
                                        }))
                                      }
                                    />
                                  </div>
                                  <div
                                    style={{
                                      marginBottom: "8px",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px"
                                    }}
                                  >
                                    <Switch
                                      size="small"
                                      checked={
                                        sftpPodSettings.emailOnFailureOnly !== false
                                      }
                                      onChange={(checked) =>
                                        setSftpPodSettings((prev) => ({
                                          ...prev,
                                          emailOnFailureOnly: checked
                                        }))
                                      }
                                    />
                                    <Text style={{ fontSize: "11px" }}>
                                      {sftpPodSettings.emailOnFailureOnly !== false
                                        ? "Email on failure only"
                                        : "Email on success and failure"}
                                    </Text>
                                  </div>
                                </div>
                                <Button
                                  type="primary"
                                  size="small"
                                  icon={<CheckOutlined />}
                                  loading={sftpPodSettingsSaving}
                                  onClick={handleSaveSftpPodSettings}
                                  style={{ width: "100%", marginTop: "4px" }}
                                >
                                  Save SFTP Settings
                                </Button>
                              </div>
                            )}

                          {/* Internal Drivers per-seat configuration */}
                          {entitlement.id === INTERNAL_DRIVERS_ENTITLEMENT_ID &&
                            (entitlement.client_is_active ||
                              internalDriversSeats[INTERNAL_DRIVERS_ENTITLEMENT_ID]) && (
                              <div
                                style={{
                                  marginTop: "12px",
                                  padding: "8px",
                                  background: "#f5f5f5",
                                  borderRadius: "4px"
                                }}
                              >
                                <div style={{ marginBottom: "8px" }}>
                                  <Text
                                    style={{
                                      fontSize: "11px",
                                      fontWeight: "500",
                                      display: "block",
                                      marginBottom: "4px"
                                    }}
                                  >
                                    Number of Seats:
                                  </Text>
                                  <InputNumber
                                    min={0}
                                    value={
                                      internalDriversSeats[
                                        INTERNAL_DRIVERS_ENTITLEMENT_ID
                                      ]?.seats || 0
                                    }
                                    onChange={(value) =>
                                      handleInternalDriversSeatsChange(
                                        INTERNAL_DRIVERS_ENTITLEMENT_ID,
                                        "seats",
                                        value
                                      )
                                    }
                                    style={{ width: "100%" }}
                                    size="small"
                                  />
                                </div>
                                <div style={{ marginBottom: "8px" }}>
                                  <Text
                                    style={{
                                      fontSize: "11px",
                                      fontWeight: "500",
                                      display: "block",
                                      marginBottom: "4px"
                                    }}
                                  >
                                    Cost per Seat ($):
                                  </Text>
                                  <InputNumber
                                    min={0}
                                    step={0.01}
                                    precision={2}
                                    value={
                                      internalDriversSeats[
                                        INTERNAL_DRIVERS_ENTITLEMENT_ID
                                      ]?.perSeatCost || 0
                                    }
                                    onChange={(value) =>
                                      handleInternalDriversSeatsChange(
                                        INTERNAL_DRIVERS_ENTITLEMENT_ID,
                                        "perSeatCost",
                                        value
                                      )
                                    }
                                    style={{ width: "100%" }}
                                    size="small"
                                  />
                                </div>
                                <Button
                                  type="primary"
                                  size="small"
                                  icon={<CheckOutlined />}
                                  loading={
                                    internalDriversSeatsSaving[
                                      INTERNAL_DRIVERS_ENTITLEMENT_ID
                                    ]
                                  }
                                  onClick={() =>
                                    handleSaveInternalDriversSeats(
                                      INTERNAL_DRIVERS_ENTITLEMENT_ID
                                    )
                                  }
                                  style={{ width: "100%", marginTop: "4px" }}
                                >
                                  Save Seats
                                </Button>
                              </div>
                            )}
                        </div>
                        <Switch
                          checked={entitlement.client_is_active || false}
                          onChange={(checked) =>
                            handleRealEntitlementToggle(entitlement.id, checked)
                          }
                          size="small"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                  {entitlementsLoading
                    ? "Loading entitlements..."
                    : "No entitlements available"}
                </div>
              )}
            </Card>
          </Spin>
        </div>
      </Form>
      <style>{`
        .entitlements-card {
          margin-bottom: 16px;
        }

        .entitlements-list {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }

        @media (max-width: 1200px) {
          .entitlements-list {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .entitlements-list {
            grid-template-columns: 1fr;
          }
        }

        .entitlement-toggle {
          padding: 12px 16px;
          border: 1px solid #d9d9d9;
          border-radius: 8px;
          background: #fafafa;
        }

        .entitlement-content {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }

        .entitlement-info {
          flex: 1;
          margin-right: 16px;
        }

        .billing-toggle {
          padding: 16px;
          border: 1px solid #d9d9d9;
          border-radius: 8px;
          background: #fafafa;
        }

        .billing-toggle.disabled-toggle {
          background: #f0f0f0;
          border-color: #e0e0e0;
          opacity: 0.7;
        }

        .toggle-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .toggle-info {
          flex: 1;
        }

        .toggle-info h5 {
          margin-bottom: 4px !important;
        }

        .blocked-switch.ant-switch-checked {
          background-color: #ff4d4f !important;
        }
        .blocked-switch.ant-switch-checked:hover {
          background-color: #ff7875 !important;
        }
        .blocked-switch.ant-switch-checked:focus {
          box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2) !important;
        }

        /* Responsive 2-column layout */
        .settings-row {
          display: flex;
          gap: 16px;
          margin-bottom: 24px;
        }

        .settings-left-column,
        .settings-right-column {
          flex: 1;
        }

        @media (max-width: 768px) {
          .settings-row {
            flex-direction: column;
          }

          .settings-left-column,
          .settings-right-column {
            flex: none;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
});

BillingSettingsTab.displayName = "BillingSettingsTab";
export default BillingSettingsTab;
